import {DEFAULT_CULTURE_NAME} from "../app/consts";
import {BaseManager} from "../../infrastructure/managers/base-manager";
import {BaseLinkModel} from "../../infrastructure/models/link/base-link-model";
import {computed, makeObservable, observable} from "mobx";
import {RouteModel} from "../components/router/models/route-model";
import {BaseUrlModel} from "../../infrastructure/models/link/base-url-model";
import {HistoryManager} from "./history-manager";
import {DomUtils} from "../../infrastructure/utils/dom-utils";
import {LoggerUtils} from "../../infrastructure/utils/logger/logger-utils";
import {SiteConfig} from "../bootstrap/models/site-config";
import {DeviceViewModel} from "../models/device.view-model";
import {AppStore} from "../app/app-store";
import {singleton} from "../../infrastructure/decorators/singleton";
import {runInAction} from "mobx"
import {ROUTE_TYPES} from "../consts";

@singleton
export class NavigationManager extends BaseManager {
  history: HistoryManager;
  onCurrentLinkModelChangeCallbacks: Array<Function>;
  @observable _currentLinkModel: BaseLinkModel;

  static injectables = [...BaseManager.injectables, AppStore, HistoryManager];

  constructor(
    domUtils: DomUtils,
    logger: LoggerUtils,
    siteConfig: SiteConfig,
    device: DeviceViewModel,
    appStore: AppStore,
    history: HistoryManager) {
    super(domUtils, logger, siteConfig, device);

    this._currentLinkModel = null;
    this.appStore = appStore;
    this.history = history;
    this.onCurrentLinkModelChangeCallbacks = [];

    makeObservable(this);
  }

  set currentLinkModel(model: BaseLinkModel) {
    const isValid = model.routeType !== ROUTE_TYPES.main || (model.name !== this._currentLinkModel?.parent);

    if (!this._currentLinkModel || (!this._currentLinkModel.equals(model) && isValid)) {
      this.onCurrentLinkModelChangeCallbacks.forEach((callback) => callback && callback(model));

      runInAction(() => {
        this._currentLinkModel = model;
      })
    }
  }

  registerToCurrentLinkModelChange(callback: Function) {
    this.onCurrentLinkModelChangeCallbacks.push(callback);
  }

  @computed get currentLinkModel(): BaseLinkModel {
    return this._currentLinkModel;
  }

  goBack() {
    const canGoBack = this.history.hasBack;

    if (canGoBack) {
      this.history.goBack();
    }

    return canGoBack;
  }

  setUrlLang(lang) {
    const path = this.history.currentScreen || window.location.pathname;
    const splittedPath = path.split('/');

    splittedPath.shift();
    splittedPath.shift();

    const isDefaultCulture = lang.CultureName === DEFAULT_CULTURE_NAME;
    const prefix = isDefaultCulture ? '' : '/' + lang.CultureName;

    const nextPath = prefix + '/' + splittedPath.join('/');

    this.history.replace(nextPath);
  }

  gotoRoute(route: RouteModel, model: BaseLinkModel, replace = false, newWindow = false) {
    const url: String = this.createUrl(route.path, model, true);

    this.gotoUrl(url, replace || model.replace, newWindow);
  }

  goto(model: BaseLinkModel, replace = false, newWindow = false) {
    const url: String = this.createUrl(model);

    this.gotoUrl(url, replace || model.replace, newWindow);
  }

  gotoUrl(url: String, replace = false, newWindow = false) {
    if (newWindow) {
      window.open(url, '_blank');
      return;
    }
    if (replace) {
      this.history.replace(url);
    } else {
      this.history.push(url);
    }
  }

  get cultureName(): String {
    const cultureName = this.appStore.state.locale.language.cultureName;

    return cultureName;
  }

  resolveUrl(path: String, link: BaseLinkModel, useHash: Boolean = false) {
    let resolvePath = '';

    path?.split('/').forEach((pathParam: String, index: Number) => {
      resolvePath += this.resolveUrlParameter(link, pathParam, index === 0);
    });

    let uri: String = resolvePath || '/';

    if (useHash) {
      const hashUrl: String = this.resolveHashParamsUrl(link);
      uri = `${uri}${hashUrl}`;
    }

    return uri;
  }

  resolveHashParamsUrl(link: BaseLinkModel) {
    let hashUrl = ''

    if (link.hashParams) {
      hashUrl = link.hash;
    }

    return hashUrl
  }

  resolveUrlParameter(link: BaseLinkModel, pathParam: String, isFirstParam: Boolean): string {
    const isParam: Boolean = pathParam.includes(':');
    const isEmptyParam = pathParam === "";
    let urlParameter = '';

    if (isFirstParam && !isEmptyParam && !isParam) {
      urlParameter = pathParam
    } else {
      if (isParam) {
        const paramName = pathParam.remove(':').replace('?', '');
        const params = link.params || {};

        const paramValue = params[paramName];
        const decoded = this.fullyDecodeURIComponent(paramValue);
        const paramForUrl = decoded?.encodePathSegment();

        urlParameter = paramValue ? (isFirstParam ? '' : '/') + paramForUrl : '';
      } else if (pathParam) {
        urlParameter = (isFirstParam ? '' : '/') + pathParam;
      }
    }

    return urlParameter;
  }

  fullyDecodeURIComponent(val: String): String {
    if (!val) return val;

    try {
      let decoded = val;
      let prev;

      do {
        prev = decoded;
        decoded = decodeURIComponent(decoded);
      } while (decoded !== prev);

      return decoded;

    } catch (e) {
      return val;
    }
  }

  get search() {
    return this.history.search;
  }

  createUrl(path: String, linkModel: BaseLinkModel, useHash: Boolean = false) {
    const isUrlModel = linkModel instanceof BaseUrlModel;
    const url: String = isUrlModel ? linkModel.toString() : this.resolveUrl(path, linkModel, useHash);

    return url;
  }
}
